import { lazy, Suspense } from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import ProtectedPageGate from "@/components/feature/ProtectedPageGate";
import CaseVersions from "@/components/feature/CaseVersions";
import { protectedPage, ProtectedLoadBoundary } from "@/protected/loader";
import { MOVED_PAGES } from "@/lib/casePages";
import { CaseMeta, Moved } from "./caseRoutes";

// NDA case studies: shipped encrypted, decrypted only after the password.
const SkillLabPage = protectedPage("skilllab");
const PMPPage = protectedPage("pmp");
const AccessibilityPage = protectedPage("accessibility");
const SDSPage = protectedPage("sds");

const NotFound = lazy(() => import("../pages/NotFound"));
const Home = lazy(() => import("../pages/home/page"));
const AboutPage = lazy(() => import("../pages/about/page"));
const AICopilotPage = lazy(() => import("../pages/ai-copilot/page"));
// The Design System section: one layout, a page per topic (see src/pages/design-system/pages.ts).
const DesignSystemSection = lazy(() => import("../pages/design-system/layout"));
const PortfolioDSPage = lazy(() => import("../pages/portfolio-design-system/page"));
// Articles: the index and one page per article file in src/content/articles.
const ArticlesIndex = lazy(() => import("../pages/articles/page"));
const ArticleRoute = lazy(() => import("../pages/articles/article"));
// Public short versions of the NDA case studies (no screens, NDA-safe).
const GuardiansSummary = lazy(() => import("../pages/case-summaries/GuardiansSummary"));
const PMPSummary = lazy(() => import("../pages/case-summaries/PMPSummary"));
const AccessibilitySummary = lazy(() => import("../pages/case-summaries/AccessibilitySummary"));
const SDSSummary = lazy(() => import("../pages/case-summaries/SDSSummary"));
// Short versions of the public case studies: the full one opens first, "?view=short" opens the short one.
const MetaProductSummary = lazy(() => import("../pages/case-summaries/MetaProductSummary"));
const PortfolioDSSummary = lazy(() => import("../pages/case-summaries/PortfolioDSSummary"));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-950 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <SuspenseWrapper><Home /></SuspenseWrapper>,
  },
  // About holds the contacts: /contact opens the same page at the contact box (old links land there).
  ...["/about", "/contact"].map((path) => ({
    path,
    element: <SuspenseWrapper><AboutPage /></SuspenseWrapper>,
  })),
  {
    // The home is the projects. The old address of that section leads to it.
    path: "/projects",
    element: <Navigate to="/" replace />,
  },
  {
    // MIITY was a case study. It is an article now, and its old address leads there.
    path: "/miity",
    element: <Navigate to="/articles/why-is-a-train-only-for-transport" replace />,
  },
  // The case studies had shorter addresses until 5 October 2026. Each old one leads to its page.
  ...MOVED_PAGES.map(({ from, to }) => ({ path: from, element: <Moved to={to} /> })),
  {
    path: "/skilllab-guardians-feature",
    element: <CaseMeta path="/skilllab-guardians-feature"><ProtectedPageGate summary={<SuspenseWrapper><GuardiansSummary /></SuspenseWrapper>}><ProtectedLoadBoundary><SuspenseWrapper><SkillLabPage /></SuspenseWrapper></ProtectedLoadBoundary></ProtectedPageGate></CaseMeta>,
  },
  {
    path: "/skilllab-counselor-portal",
    element: <CaseMeta path="/skilllab-counselor-portal"><ProtectedPageGate summary={<SuspenseWrapper><PMPSummary /></SuspenseWrapper>}><ProtectedLoadBoundary><SuspenseWrapper><PMPPage /></SuspenseWrapper></ProtectedLoadBoundary></ProtectedPageGate></CaseMeta>,
  },
  {
    path: "/skilllab-accessibility",
    element: <CaseMeta path="/skilllab-accessibility"><ProtectedPageGate summary={<SuspenseWrapper><AccessibilitySummary /></SuspenseWrapper>}><ProtectedLoadBoundary><SuspenseWrapper><AccessibilityPage /></SuspenseWrapper></ProtectedLoadBoundary></ProtectedPageGate></CaseMeta>,
  },
  {
    path: "/meta-product",
    element: <CaseMeta path="/meta-product"><CaseVersions short={<SuspenseWrapper><MetaProductSummary /></SuspenseWrapper>}><SuspenseWrapper><AICopilotPage /></SuspenseWrapper></CaseVersions></CaseMeta>,
  },
  {
    path: "/skilllab-design-system",
    element: <CaseMeta path="/skilllab-design-system"><ProtectedPageGate summary={<SuspenseWrapper><SDSSummary /></SuspenseWrapper>}><ProtectedLoadBoundary><SuspenseWrapper><SDSPage /></SuspenseWrapper></ProtectedLoadBoundary></ProtectedPageGate></CaseMeta>,
  },
  {
    path: "/portfolio-design-system",
    element: <CaseVersions short={<SuspenseWrapper><PortfolioDSSummary /></SuspenseWrapper>}><SuspenseWrapper><PortfolioDSPage /></SuspenseWrapper></CaseVersions>,
  },
  {
    // One route for the whole section, so the side menu stays in place from page to page.
    path: "/design-system/:page?",
    element: <SuspenseWrapper><DesignSystemSection /></SuspenseWrapper>,
  },
  {
    path: "/articles",
    element: <SuspenseWrapper><ArticlesIndex /></SuspenseWrapper>,
  },
  {
    path: "/articles/:slug",
    element: <SuspenseWrapper><ArticleRoute /></SuspenseWrapper>,
  },
  {
    path: "*",
    element: <SuspenseWrapper><NotFound /></SuspenseWrapper>,
  },
];

export default routes;